import { CommonModule } from '@angular/common';
import { Component, NgZone } from '@angular/core';
import { IonApp, IonMenu, IonRouterOutlet, IonSplitPane, Platform } from '@ionic/angular/standalone';
import { AuthenticationService } from '@shared/services/authentication/authentication.service';
import { ChatService } from '@shared/services/chat/chat.service';
import { ClockAndVisitOperationService } from '@shared/services/clock-and-visit/clock-and-visit-operation.service';
import { ConfigService } from '@shared/services/config/config.service';
import { LocalService } from '@shared/services/local/local.service';
import { LogService } from '@shared/services/log/log.service';
import { NativeStorageService } from '@shared/services/storage/native-storage.service';
import { NavControllerService } from '@shared/services/navigation/nav-controller.service';
import { OfferService } from '@shared/services/offer/offer.service';
import { ProductService } from '@shared/services/product/product.service';
import { PushNotificationService } from '@shared/services/push-notification/push-notification.service';
import { environment as ENV } from '../environments/environment';
import { PhotoService } from '@shared/services/photo/photo.service';
import { UtilsService } from '@shared/services/utils/utils.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { RuleService } from '@shared/services/rule/rule.service';
import { GraphqlService } from '@shared/services/http/graphql.service';
import { SubscriptionService } from '@shared/services/subscription/subscription.service';
import { NavigationMenuComponent } from 'src/app/components/navigation-menu/navigation-menu.component';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    CommonModule,
    IonApp,
    IonMenu,
    IonRouterOutlet,
    IonSplitPane,
    NavigationMenuComponent,
  ],
})
export class AppComponent {

  private unrestrictedRoutes = ['/report-link', '/signup', '/signup-pro', '/change-password', '/client-notifications'];

  private isPushNavigationRegistered = false;

  constructor(
    private platform: Platform,
    private clockService: ClockAndVisitOperationService,
    private authService: AuthenticationService,
    private nativeStorage: NativeStorageService,
    private navCtrl: NavControllerService,
    private productService: ProductService,
    private localService: LocalService,
    private graphqlService: GraphqlService,
    private pushNotificationService: PushNotificationService,
    private chatService: ChatService,
    private offerService: OfferService,
    private photoService: PhotoService,
    private logService: LogService,
    public configService: ConfigService,
    private ngZone: NgZone,
    private utils: UtilsService,
    private ruleService: RuleService,
    private subscriptionService: SubscriptionService,
  ) {
    this.platform.ready().then(async () => {
      const pathname = window.location.pathname;
      if (!this.unrestrictedRoutes.some(route => pathname === route || pathname.startsWith(`${route}/`))) {
        this.configure(this.platform);
        this.logService.log('App started');
        this.init();
      } else {
        this.configure(this.platform);
      }
    });
  }

  public getRole() {
    return this.authService.role;
  }

  public getPlan() {
    return this.authService.getPlan();
  }

  private async init() {
    this.subscriptionService.init();
    this.authService.init().then((hasToken) => {
      if (hasToken) {
        this.logService.log('App started with token');
        this.subscriptionService.queryUserSubscriptionEnd()
          .catch((error) => this.logService.logError(error));
        this.initializePostAuthServices();
      } else {
        this.logService.log('App started without token');
        this.pushNotificationService.init();
        this.chatService.init();
        this.ruleService.init();
        this.offerService.init();
        this.productService.init();
      }
      return this.loadStart();
    });
  }

  private initializePostAuthServices() {
    this.productService.init();
    this.localService.init();
    this.pushNotificationService.init();
    this.chatService.init();
    this.ruleService.init();
    if (this.configService.isMobileApp && this.authService.role === 'EMPLOYEE') {
      this.photoService.initializeSelfieSubmission();
      this.configService.setStockType('COMPLETE');
    }
  }

  private async loadStart() {
    let startPage;


    if (!this.configService.isMobileApp) {
      startPage = window.location.pathname;
    } else {
      startPage = await this.nativeStorage.getItem('startPage').catch(() => undefined);
    }

    await this.startProApp(startPage);

  }

  private configure(plataform: Platform) {
    this.configService.configureBackButton(() => this.navCtrl.pop());
    this.configService.platformName = plataform.is('android') ? 'android' : 'ios';
    const isMobile = this.platform.is('ios')
      || this.platform.is('android')
      || this.platform.is('capacitor')
      || this.platform.is('cordova');
    this.configService.isMobileApp = isMobile && !this.platform.is('mobileweb');
    this.configService.apiUrl = ENV.apiUrl;
    this.configService.webUrl = ENV.webUrl;
    this.configService.inferencesApiUrl = ENV.inferencesApiUrl;
    this.configService.mode = ENV.mode;
    this.configService.version = ENV.version;
    this.configService.isProApp = true;
  }

  private async startProApp(startPage) {
    this.logService.log(`Starting app with start page ${startPage}`);
    if (!startPage) {
      return this.navCtrl
        .setRoot('login')
        .then(() => {
          this.registerPushNotificationNavigation();
          this.splashScreenHide();
        });
    }
    await this.clockService.init();
    if (!this.graphqlService.isAuthenticated()) {
      this.logService.log('No authenticated client');
      return this.navigateToStartPage(startPage);
    }
    this.logService.log('Authenticated client');
    if (this.authService.role !== 'EMPLOYEE') {
      this.navigateToStartPage(startPage);
    } else {
      this.verifyClockInTimeout(startPage);
    }
  }

  private verifyClockInTimeout(startPage) {
    return this.nativeStorage
      .getItem('lastClockInOpetation')
      .then((lastClockInOperation) => {
        const clockInDateTime =
          lastClockInOperation.date + 'T' + lastClockInOperation.time;
        const clockInDuration = Math.floor((Date.now() - new Date(clockInDateTime).getTime()) / 60_000);
        if (
          lastClockInOperation.type === 'START' &&
          clockInDuration > this.clockService.clockInTimeout
        ) {
          return this.verifyUnfinishedClock();
        }
        this.navigateToStartPage(startPage);
      })
      .catch(() => this.navigateToStartPage(startPage));
  }

  private verifyUnfinishedClock() {
    return this.nativeStorage
      .getItem('lastVisitCheckInOperation')
      .catch(() => undefined)
      .then((lastVisitCheckInOperation) => {
        if (
          lastVisitCheckInOperation &&
          lastVisitCheckInOperation.type === 'START'
        ) {
          this.nativeStorage.getItem('local').then((local) => {
            this.navCtrl
              .setRoot('clock-and-visit-operation', {
                local,
                isLastVisitUnfinished: true,
              })
              .then(() => this.splashScreenHide());
          });
        } else {
          this.clockService.runAutomaticClockOut().then(() => {
            this.nativeStorage.setItem('startPage', 'ClockInPage');
            this.navCtrl
              .setRoot('clock-and-visit-operation')
              .then(() => this.splashScreenHide());
          });
        }
      });
  }

  private async navigateBasedOnPath(startPage) {
    const pathname = startPage;
    if (!this.authService.isAuthenticated()) {
      this.navCtrl.setRoot('login');
      return;
    }
    this.configService.isMenuShown = true;
    if (pathname === '/' || pathname === '/splash-screen') {
      const isProPlan = this.authService.isProPlan();
      const initialPage = isProPlan ? 'leads' : 'analyzes-admin';
      return this.navCtrl.setRoot(initialPage);
    } else {
      this.navCtrl.setNavigationStack([
        { page: pathname, params: {} },
      ]);
    }
  }

  private async navigateToStartPage(startPage) {
    if (!this.configService.isMobileApp) {
      return this.navigateBasedOnPath(startPage);
    }
    this.logService.log(`Setting root page to ${startPage}`);
    this.configService.isMenuShown = true;
    let navigationPromise;
    switch (startPage) {
      case 'PlacesPage':
        navigationPromise = this.navCtrl.setRoot('places');
        break;
      case 'VisitPage':
        navigationPromise = this.navCtrl.setRoot('visit');
        break;
      case 'ClockInPage':
        navigationPromise = this.navCtrl.setRoot(
          'clock-and-visit-operation',
        );
        break;
      case 'selfie-photo-verification':
        navigationPromise = this.navCtrl.setRoot('selfie-photo-verification');
        break;
      case 'photo-instructions':
        navigationPromise = this.navCtrl.setRoot('photo-instructions');
        break;
      case 'address-verification':
        navigationPromise = this.navCtrl.setRoot('address-verification');
        break;
      default:
        navigationPromise = this.navCtrl.setRoot('login');
    }
    navigationPromise.then(() => {
      this.registerPushNotificationNavigation();
      return this.splashScreenHide();
    });
  }

  private registerPushNotificationNavigation() {
    if (this.isPushNavigationRegistered) {
      return;
    }
    this.isPushNavigationRegistered = true;
    this.pushNotificationService.onMessageReceived((data) => {
      this.logService.log('Push notification received', data);
      this.ngZone.run(() => {
        if (data.supportTicketId) {
          this.navCtrl.push(`support-ticket/${data.supportTicketId}`);
        } else if (data.leadId) {
          if (data.tap) {
            this.navCtrl.push(`/lead-order/${data.leadId}`);
          } else {
            this.utils.presentToast(data.title);
          }
        } else if (data.analyzeId) {
          this.navCtrl.push('report-link?analysis=' + data.analyzeId);
        }
      });
    });
  }

  private splashScreenHide() {
    if (this.configService.isMobileApp) {
      SplashScreen.hide();
    }
  }

}
